import { useState, useEffect, useMemo } from 'react';
import { View, SafeAreaView, ActivityIndicator, useWindowDimensions } from 'react-native';
import THEME from '../../config/theme';
import {
  doc,
  getDoc,
} from 'firebase/firestore';
import { firestore } from '../../services/firebase';
import { StandardText } from '../../config/theme/globalStyles';
import { useCustomTheme } from '../../contexts/useCustomTheme';
import { useAuthUser } from '../../contexts/useAuthUser';
import VideoPlayer from '../../components/VideoPlayer';
import { useNavigation } from '@react-navigation/native';
import Button from '../../components/Button';

export function Video({ route }) {
  const { cardId, categoryId, selectedVideo } = route.params;
  const { customColor, customFont } = useCustomTheme();
  const navigation = useNavigation();

  const [isVideoReady, setIsVideoReady] = useState(false);

  const { width, height } = useWindowDimensions();

  //  // Largura máxima “bonita” no desktop, mas responsiva no mobile
  const maxPlayerWidth = Math.min(width - 32, 1280);
  const playerHeight = maxPlayerWidth / (16 / 9);

  const [card, setCard] = useState();
  const [video, setVideo] = useState(null);
  // Sessão e documento `users/{uid}` já resolvidos no boot (AuthUserProvider).
  const { user: userDoc, userId, isLogged, isLoadingUser } = useAuthUser();
  const user = useMemo(
    () => (isLogged && userDoc ? { id: userId, ...userDoc } : undefined),
    [isLogged, userDoc, userId]
  );
  const [isLoading, setIsLoading] = useState(true);

  const [isCardLoaded, setIsCardLoaded] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const isUserLoaded = !isLoadingUser;
  // Considera "não logado" quando não há user OU quando é anônimo.
  const needsLogin = !isLoadingUser && !isLogged;

  const toDate = (seconds) => {
    const date = new Date(1970, 0, 1);
    date.setSeconds(seconds);
    return date;
  };

  const fetchCard = async () => {
    const docRef = doc(firestore, 'categories', categoryId, 'cards', cardId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) return;
    const cardData = docSnap.data();
    setCard(cardData);
    setIsCardLoaded(true);
    console.log("cardData is", cardData)

  };

  const fetchVideo = async () => {
    const docRef = doc(
      firestore,
      'categories',
      categoryId,
      'cards',
      cardId,
      'videos',
      selectedVideo
    );
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
         setIsVideoLoaded(true);
         setIsVideoReady(false);
         setVideo(null);
         return;
     }
      
    const videoData = docSnap.data();

    console.log("Video data is", videoData)
    setVideo(videoData);
    setIsVideoLoaded(true);
  };

  useEffect(() => {
    if (!video || Array.isArray(video)) return; 

    const src =
      video?.videoInfo?.url ||
      video?.pandaVideoMetadata?.video_player ||
      video?.link ||
      video?.url;

    const isValidLink = typeof src === 'string' && src.trim().length > 0;
    const hasPDF = video?.pdf?.url && video?.pdf?.url.length > 0;

    const hasPlayableSource =
      isValidLink ||
      video?.type === 'livestream' ||
      (video?.meetingNumber && video?.meetingNumber.length > 0) ||
      hasPDF;

    if (hasPlayableSource) {
      setIsVideoReady(true);
    }
  }, [video]);

  useEffect(() => {
    fetchCard();
    fetchVideo();
  }, [categoryId, cardId, selectedVideo]);

  useEffect(() => {
    if (isCardLoaded && isVideoLoaded && isUserLoaded && isVideoReady) {
      setIsLoading(false);
    }
  }, [isCardLoaded, isVideoLoaded, isUserLoaded, isVideoReady]);

  const LoginCTA = () => (

    <View
      style={{
        minHeight: height,
        width: '100%',
        padding: 16,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: customColor?.background?.app ?? THEME.COLORS.BACKGROUND_MAIN,
      }}
    >
      <StandardText
        style={{
          fontFamily: customFont?.fontFamilySubtitle ?? THEME.FONTFAMILY.MEDIUM,
          textAlign: 'center',
          marginBottom: 16,
        }}
        color={customColor?.text?.auth ?? THEME.COLORS.TEXT_ABOUT}
      >
        Você precisa estar logado para acessar este conteúdo!
      </StandardText>

      <Button
        title={"Fazer Login"}
        onPress={() =>
          navigation.navigate('Login', {
            returnTo: 'Watch',
            categoryId: categoryId,
            cardId: cardId,
            selectedVideo: selectedVideo
          })
        }
      ></Button>
    </View>
  );

  // Antes de qualquer loading: `needsLogin` agora é conhecido já na primeira
  // renderização (a sessão vem do boot), então o visitante vai direto para o
  // CTA em vez de esperar um vídeo que ele não tem permissão de carregar.
  if (needsLogin) {
    return <LoginCTA />;
  }

  if (isLoading) {
    return (
      <ActivityIndicator
        style={{
          flex: 1,
          backgroundColor:
            customColor && customColor.background?.app
              ? customColor.background.app
              : THEME.COLORS.BACKGROUND_MAIN,
          justifyContent: 'center',
        }}
        color={
          customColor && customColor.primary
            ? customColor.primary
            : THEME.COLORS.PRIMARY_800
        }
      />
    );
  }

  const OutsideView = () => {

    return (
      <View
        style={{
          alignItems: 'center',           
          justifyContent: 'center',       
          backgroundColor: customColor?.background?.app ?? THEME.COLORS.BACKGROUND_MAIN,
          width: width,
          height: height
        }}
      >
          <VideoPlayer url={video?.url} />
      </View>
    );
  };

  return (
    <SafeAreaView
      style={{
        backgroundColor:
          customColor && customColor.background?.app
            ? customColor.background.app
            : THEME.COLORS.BACKGROUND_MAIN,
        flexGrow: 1,
      }}
    >
      {(card?.productIds &&
        card?.productIds?.length > 0 &&
        card?.productIds?.filter((cardProductId) => {
          const matchingUserProducts = user?.productIds?.filter(
            (userProduct) => userProduct.productId === cardProductId
          );
          if (!matchingUserProducts || matchingUserProducts.length === 0) {
            return false; // O usuário não possui este produto, então não tem acesso
          }
          // Se não houver data de expiração, ou se alguma data de expiração for maior que a data atual, o usuário tem acesso
          return matchingUserProducts.some(
            (userProduct) =>
              !userProduct.expirationDate ||
              toDate(userProduct.expirationDate.seconds) > new Date()
          );
        }).length > 0) ||
        (card?.plans &&
          card?.plans.length > 0 &&
          card?.plans.filter(
            (plan) => (plan.priceId || plan.productId) == user?.plan
          ).length > 0) ||
        (user?.courses &&
          user?.courses.length > 0 &&
          user?.courses.filter(
            (course) =>
              card?.coursesCard?.some(
                (itemToBeRemoved) =>
                  (itemToBeRemoved.priceId || itemToBeRemoved.productId) ===
                  (course.priceId || course.productId)
              ) && toDate(course.dueDate.seconds) > new Date()
          ).length > 0) ||
        (card?.plans == null &&
          card?.coursesCard == null &&
          card?.productIds == null) ||
        user?.isAdmin == true ? (
        <OutsideView />
      ) : (
        <View
          style={{
            justifyContent: 'center',
            flex: 1,
            alignItems: 'center',
            marginHorizontal: '0.5rem',
          }}
        >
          <StandardText
            style={{
              fontFamily:
                customFont && customFont.fontFamilySubtitle
                  ? customFont.fontFamilySubtitle
                  : THEME.FONTFAMILY.MEDIUM,
            }}
            color={
              customColor && customColor.text && customColor.text.auth
                ? customColor.text.auth
                : THEME.COLORS.TEXT_ABOUT
            }
          >
            Ops, você não possui acesso a esse conteúdo!
          </StandardText>
        </View>
      )}
    </SafeAreaView>
  );
}

